import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, pluck, tap } from 'rxjs/operators';
import { Integration } from '../objects/Integration';
import { CompanyBranding, PremiumApi, PremiumGroupSlot, RequestApiIntegration } from '../types';
import { APP_CONFIG } from './config.service';
import { IntegrationService } from './integration.service';
import { WhiteLabelService } from './white-label.service';

@Injectable({
  providedIn: 'root',
})
export class IntegrationApiService {
  private url = 'http://localhost:8080/api/';

  constructor(
    private http: HttpClient,
    private integrationService: IntegrationService,
    private whiteLabelService: WhiteLabelService,
  ) {
    APP_CONFIG()
      .pipe(pluck('baseUri'))
      .subscribe((url: string) => (this.url = url));
  }

  public getIntegration(): Observable<Integration> {
    return this.http.get(this.url + 'integration/client').pipe(
      tap((integrationData: Integration) => this.handleSuccess(integrationData)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public getPremiumApi(premiumApiId: number): Observable<PremiumApi> {
    return this.http.get(this.url + `integration/premium-api/${premiumApiId}`).pipe(
      tap((premiumApiData: PremiumApi) => this.handleSuccessGetPremiumApi(premiumApiData)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public postPremiumGroupSlot(
    premiumApiId: number,
    data: { email: string; name: string; lang: string },
  ): Observable<any> {
    return this.http.post(this.url + `integration/premium-api/${premiumApiId}/assign`, data).pipe(
      tap((premiumGroupSlots: PremiumGroupSlot[]) =>
        this.handleSuccessPostPremiumGroupSlot(premiumGroupSlots),
      ),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public postPremiumGroupMultipleSlot(
    premiumApiId: number,
    data: { users: { email: string; name: string }[]; lang: string },
  ): Observable<any> {
    return this.http
      .post(this.url + `integration/premium-api/${premiumApiId}/assign/bulk`, data)
      .pipe(
        tap((response) => response),
        catchError((error: HttpErrorResponse) => this.handleError(error)),
      );
  }

  public postDeletePremiumGroupSlot(
    premiumApiId: number,
    premiumGroupSlotId: number,
    data: { lang: string },
  ): Observable<any> {
    return this.http
      .post(
        this.url + `integration/premium-api/${premiumApiId}/slot/${premiumGroupSlotId}/clear`,
        data,
      )
      .pipe(
        tap((premiumGroupSlots: PremiumGroupSlot[]) =>
          this.handleSuccessPostPremiumGroupSlot(premiumGroupSlots),
        ),
        catchError((error: HttpErrorResponse) => this.handleError(error)),
      );
  }

  public postRenamePremiumGroupSlot(
    premiumApiId: number,
    premiumGroupSlotId: number,
    name: string,
  ): Observable<any> {
    return this.http
      .post(
        this.url + `integration/premium-api/${premiumApiId}/slot/${premiumGroupSlotId}/rename`,
        { name },
      )
      .pipe(
        tap((premiumGroupSlots: PremiumGroupSlot[]) =>
          this.handleSuccessPostPremiumGroupSlot(premiumGroupSlots),
        ),
        catchError((error: HttpErrorResponse) => this.handleError(error)),
      );
  }

  public cancelApiSubscription(premiumApiId: number, lang: string) {
    return this.http
      .post(this.url + `integration/premium-api/${premiumApiId}/cancel`, { lang })
      .pipe(
        tap((cancelData) => cancelData),
        catchError((error: HttpErrorResponse) => this.handleError(error)),
      );
  }

  public getCompanyBranding(id: string): Observable<any> {
    return this.http.get(this.url + `integration/company-branding/${id}`, {}).pipe(
      tap((data: CompanyBranding) => this.handleSuccessWhiteLabel(data)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public requestApiIntegration(data: RequestApiIntegration): Observable<any> {
    return this.http.post(this.url + `integration/client/request`, data).pipe(
      tap((response) => response),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  private handleSuccess(data: Integration) {
    this.integrationService.setState(data);
    return data;
  }
  private handleSuccessWhiteLabel(data: CompanyBranding) {
    this.whiteLabelService.setState(data);
    return data;
  }

  private handleSuccessGetPremiumApi(data: PremiumApi) {
    return data;
  }

  private handleSuccessPostPremiumGroupSlot(data: PremiumGroupSlot[]) {
    return data;
  }

  private handleError(error: HttpErrorResponse) {
    return throwError(error);
  }
}
